import React, { Component } from "react";
import { APIClient } from "../../utils/APIClient";
import { API_URL } from "../../settings/local_settings";
import { isOk } from "../../utils/apiResponse";
import { AgencyHeader } from "./AgencyHeader";
import { AgencyTabs } from "./AgencyTabs";
import { Spinner } from "../Spinner";
import { LoadErrorState } from "../elements/LoadErrorState";
import { withTranslation } from 'react-i18next';
import { BillingHistorySection } from "./subscription/BillingHistorySection";
import PlanChangeSection from "./subscription/PlanChangeSection";
import CheckoutForm from "./subscription/CheckoutForm";
import BankTransferInstructions from "./subscription/BankTransferInstructions";
import PendingPaymentBanner from "./subscription/PendingPaymentBanner";
import { CancelSubscriptionSection } from "./subscription/CancelSubscriptionSection";
import { TrialCodeCard } from "../trial_code/TrialCodeForm";
import { PaidSubscriptionView } from "./subscription/paid/PaidSubscriptionView";
import { hasUnpaid } from "./subscription/paid/subscriptionPageModel";
import { LocalStorage } from "../../utils/LocalStorage";
import { toast } from "react-toastify";

/**
 * Agencia › Suscripción. Decide la vista:
 * - **Prueba, vencida, checkout y transferencia recién confirmada**: la de siempre (grilla + checkout).
 * - **Agencia paga** (ni prueba ni vencida): `PaidSubscriptionView` (tanda «Suscripción y pagos», UD-16).
 *
 * Pide tres cosas (contrato §3): `agency/subscription/` al abrir (si falla, `LoadErrorState`);
 * `billing-history/` al abrir y cada 30 s mientras haya algo sin pagar, en silencio; y
 * `payment-subscription/` UNA vez al abrir la vista paga (es una consulta en vivo al proveedor: nunca entra
 * en el refresco).
 */
class AgencySubscriptionContent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            loadError: null,
            subscriptionData: null,
            billingData: null,
            billingLoading: true,
            // `{status: 'loading' | 'ok' | 'error', data}`. Con error, el próximo cobro no se dibuja.
            paymentSubscription: { status: 'loading', data: null },
            isSuperAdmin: false,
            checkoutData: null,  // null = no checkout, objeto = checkout activo
            pendingPaymentUrl: null,
            bankTransferData: null,  // datos para transferir tras confirmar checkout por transferencia
            commentSent: false,
            refreshingBilling: false,
            // Solicitud de baja EN CURSO de la agencia, o null. Viaja en el payload de suscripción
            // y se mantiene acá para que pedirla (o retirarla) se refleje sin recargar la página.
            cancellationRequest: null,
        };
    }

    handleCancellationChanged = (cancellationRequest) => {
        this.setState({ cancellationRequest: cancellationRequest || null });
    }

    // La agencia aceptó el descuento de retención en el modal de baja (UD-17): se vuelven a pedir la
    // suscripción (`active_retention_offer`) y el historial (`price.next`), así la tarjeta del plan dice el
    // descuento sin recargar. El historial, en silencio: con el spinner, la tarjeta perdía el precio un rato.
    handleRetentionAccepted = () => {
        this.loadSubscriptionData();
        this.loadBillingHistory(true);
    }

    scrollToPlans = () => {
        const plans = document.getElementById('plan-change');
        if (plans) {
            plans.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    refreshBilling = () => {
        const { t } = this.props;
        this.setState({ refreshingBilling: true });
        new APIClient(API_URL + 'agency/subscription/refresh-billing/')
            .call('POST', {})
            .then((data) => {
                if (!isOk(data)) {
                    toast.error(t('agency_subscription.refresh_billing.toast_error'));
                    return;
                }
                if (data.throttled) {
                    toast.info(t('agency_subscription.refresh_billing.toast_throttled'));
                } else {
                    LocalStorage.updateHasPaymentSuspensionWarning(data.has_payment_suspension_warning === true);
                    const updated = data.liquidations_marked_paid || 0;
                    if (updated > 0) {
                        toast.success(t('agency_subscription.refresh_billing.toast_updated', { count: updated }));
                    } else {
                        toast.info(t('agency_subscription.refresh_billing.toast_no_changes'));
                    }
                    // `refresh-billing/` borra el próximo cobro guardado (contrato §9): con el gesto del
                    // usuario —no con el refresco de fondo— se vuelve a pedir.
                    if (this.isPaidView()) this.loadPaymentSubscription({ again: true });
                }
                this.loadBillingHistory();
            })
            .catch(() => {
                toast.error(t('agency_subscription.refresh_billing.toast_error'));
            })
            .finally(() => {
                this.setState({ refreshingBilling: false });
            });
    }

    handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            this.loadBillingHistory(true);
        }
    }

    // El refresco de fondo: sólo el historial (nunca `payment-subscription`), en silencio y mientras haya
    // algo `pending`/`overdue` (o, con Tesla viejo, sin pagar).
    maybePollBillingHistory = () => {
        if (document.visibilityState !== 'visible') return;
        const { billingData } = this.state;
        if (!billingData || !billingData.available || !billingData.liquidations) return;
        if (!hasUnpaid(billingData.liquidations)) return;
        this.loadBillingHistory(true);
    }

    componentDidMount() {
        this.loadSubscriptionData();
        this.loadBillingHistory();
        this.fetchSuperAdminFlag();

        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        this.billingPollInterval = setInterval(this.maybePollBillingHistory, 30000);
    }

    componentWillUnmount() {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        if (this.billingPollInterval) {
            clearInterval(this.billingPollInterval);
            this.billingPollInterval = null;
        }
    }

    fetchSuperAdminFlag() {
        new APIClient(API_URL + 'get-logged-user-data/').call('GET').then((data) => {
            if (data && data.status === 'OK') {
                this.setState({ isSuperAdmin: data.is_super_admin === true });
            }
        });
    }

    // «Reintentar» de la pantalla de error: vuelve al loader, o el clic no se ve.
    retrySubscriptionData = () => {
        this.setState({ loading: true, loadError: null }, () => this.loadSubscriptionData());
    }

    loadSubscriptionData() {
        new APIClient(API_URL + 'agency/subscription/').call('GET').then((data) => {
            if (!isOk(data) || !data.data) {
                // Una recarga que falla con la pantalla ya dibujada (después del checkout) no la tapa.
                if (this.state.subscriptionData) return;
                // Nunca el `error_message` crudo: puede ser el `detail` en inglés de DRF.
                this.setState({ loading: false, loadError: (data && data.status) || 'ERROR' });
                return;
            }
            this.setState({
                subscriptionData: data.data,
                // `cancellation_request` es nullable y puede no venir (Backend sin deployar):
                // el `|| null` deja la pantalla igual que antes en esa ventana.
                cancellationRequest: data.data.cancellation_request || null,
                loading: false
            }, () => {
                this.scrollToPlansIfRequested();
                if (this.isPaidView()) this.loadPaymentSubscription();
            });
        });
    }

    // Una sola vez al abrir la vista paga; un reintento de la pantalla no la repite. `again` es «Actualizar
    // estado» (un gesto del usuario): si falla, se queda con lo que ya mostraba.
    loadPaymentSubscription({ again = false } = {}) {
        if (this._paymentSubscriptionRequested && !again) return;
        this._paymentSubscriptionRequested = true;
        new APIClient(API_URL + 'agency/subscription/payment-subscription/').call('GET').then((data) => {
            if (isOk(data) && !data.reason) {
                this.setState({ paymentSubscription: { status: 'ok', data: data.data || null } });
            } else if (!again) {
                this.setState({ paymentSubscription: { status: 'error', data: null } });
            }
        });
    }

    // Se llega con `#plan-change` desde el CTA «Mejorar plan» del límite de usuarios cliente. El
    // scroll nativo al fragmento ocurre al cargar la página, cuando `#plan-change` todavía no
    // existe (la pantalla arranca con un Spinner): hay que hacerlo cuando la sección aparece, una
    // sola vez. En la agencia paga ese mismo hash abre «Cambiar de plan» (`PaidSubscriptionView`).
    scrollToPlansIfRequested = () => {
        if (this._scrolledToPlans || window.location.hash !== '#plan-change') return;
        this._scrolledToPlans = true;
        this.scrollToPlans();
    }

    loadBillingHistory(silent = false) {
        if (!silent) this.setState({ billingLoading: true });
        new APIClient(API_URL + 'agency/subscription/billing-history/').call('GET').then((data) => {
            if (data.status === 'OK') {
                this.setState({
                    billingData: data.data,
                    billingLoading: false
                });
            } else if (!silent) {
                this.setState({
                    billingData: { available: false, reason: 'tesla_error' },
                    billingLoading: false
                });
            }
        }).catch(() => {
            if (!silent) {
                this.setState({
                    billingData: { available: false, reason: 'tesla_error' },
                    billingLoading: false
                });
            }
        });
    }

    handleEmailsUpdated = (updatedEmails) => {
        const bd = { ...this.state.billingData };
        if (bd.client) {
            bd.client = { ...bd.client, ...updatedEmails };
        }
        this.setState({ billingData: bd });
    }

    isPendingPaymentConfirmation() {
        if (this.state.pendingPaymentUrl) return true;
        const { billingData, billingLoading } = this.state;
        if (billingLoading || !billingData || !billingData.available) return false;
        return billingData.client?.status === 'Nuevo a confirmar';
    }

    getPaymentUrl() {
        return this.state.pendingPaymentUrl
            || this.state.billingData?.client?.subscription_url
            || null;
    }

    isExpired() {
        const { subscriptionData } = this.state;
        if (!subscriptionData) return false;
        if (!subscriptionData.subscription_valid_till) return false;
        const today = new Date();
        const validTill = new Date(subscriptionData.subscription_valid_till + 'T23:59:59');
        return today > validTill;
    }

    isPaidView() {
        const { subscriptionData } = this.state;
        return !!subscriptionData && !subscriptionData.is_free_trial && !this.isExpired();
    }

    render() {
        return (
            <section className="section-content">
                <AgencyHeader />
                <AgencyTabs activeTab="-last" />
                {this.renderBody()}
            </section>
        );
    }

    renderBody() {
        const { t } = this.props;
        const { loading, loadError, checkoutData, bankTransferData } = this.state;

        if (loading) {
            return (
                <div className="section-body h-items">
                    <div className="box-loader">
                        <Spinner color="positive" />
                    </div>
                </div>
            );
        }

        if (loadError !== null) {
            return (
                <div className="section-body sp-section-body">
                    <LoadErrorState
                        title={t('subscription_page.load_error')}
                        text={t('subscription_page.load_error_explanation')}
                        retryLabel={t('subscription_page.load_error_retry')}
                        onRetry={this.retrySubscriptionData}
                    />
                </div>
            );
        }

        if (!checkoutData && !bankTransferData && this.isPaidView()) {
            return <div className="section-body sp-section-body">{this.renderPaidView()}</div>;
        }

        return (
            <div className="section-body h-items" style={{ paddingBottom: '0em' }}>
                {this.renderContent()}
            </div>
        );
    }

    renderPaidView() {
        const { t } = this.props;
        const { subscriptionData, billingData, billingLoading, paymentSubscription, isSuperAdmin,
            cancellationRequest, refreshingBilling, commentSent } = this.state;
        const showPendingBanner = this.isPendingPaymentConfirmation();

        const topNotices = (commentSent || showPendingBanner) ? (
            <div className="sp-top-notices">
                {commentSent && (
                    <div className="sp-flash" role="status">
                        <p className="sp-flash__title">{t('checkout.comment_sent_title')}</p>
                        <p>{t('checkout.comment_sent_description')}</p>
                    </div>
                )}
                {showPendingBanner && !commentSent && (
                    <div id="pending-payments">
                        <PendingPaymentBanner subscriptionUrl={this.getPaymentUrl()} />
                    </div>
                )}
            </div>
        ) : null;

        return (
            <PaidSubscriptionView
                subscriptionData={subscriptionData}
                billingData={billingLoading ? null : billingData}
                paymentSubscription={paymentSubscription}
                isSuperAdmin={isSuperAdmin}
                cancellationRequest={cancellationRequest}
                onCancellationChanged={this.handleCancellationChanged}
                refreshingBilling={refreshingBilling}
                onRetryBilling={() => this.loadBillingHistory()}
                onRefreshBilling={this.refreshBilling}
                onBillingEmailsSaved={this.handleEmailsUpdated}
                onLiquidationUpdated={() => this.loadBillingHistory(true)}
                onRetentionAccepted={this.handleRetentionAccepted}
                topNotices={topNotices}
            />
        );
    }

    renderContent() {
        const { t } = this.props;

        const { subscriptionData, billingData, billingLoading, isSuperAdmin, checkoutData } = this.state;
        const isFreeTrial = subscriptionData.is_free_trial;
        const isExpired = this.isExpired();
        const showPendingBanner = this.isPendingPaymentConfirmation();

        // Si está en modo checkout, mostrar el formulario
        if (checkoutData) {
            return (
                <div className="section-body-item -wider">
                    <CheckoutForm
                        checkoutData={checkoutData}
                        onBack={() => this.setState({ checkoutData: null })}
                        onCheckoutComplete={(subscriptionUrl, hasComment, bankTransferData) => {
                            this.setState({
                                checkoutData: null,
                                pendingPaymentUrl: subscriptionUrl || null,
                                bankTransferData: bankTransferData || null,
                                commentSent: !!hasComment,
                            });
                            this.loadSubscriptionData();
                            this.loadBillingHistory();
                        }}
                    />
                </div>
            );
        }

        // Tras confirmar un checkout por transferencia bancaria, mostrar los datos para transferir
        if (this.state.bankTransferData) {
            return (
                <div className="section-body-item -wider">
                    <BankTransferInstructions
                        data={this.state.bankTransferData}
                        onDone={() => {
                            this.setState({ bankTransferData: null });
                            this.loadBillingHistory();
                        }}
                    />
                </div>
            );
        }

        return (
            <div className="section-body-item -wider">
                {this.state.commentSent && (
                    <div style={{
                        backgroundColor: '#eef7f6',
                        border: '1px solid #c8e6e3',
                        borderRadius: '0.5rem',
                        padding: '1rem 1.25rem',
                        marginBottom: '1.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75em',
                    }}>
                        <i className="fa fa-check-circle" style={{ color: '#259D92', fontSize: '1.3rem' }} />
                        <div>
                            <strong style={{ fontSize: '0.95rem' }}>{t('checkout.comment_sent_title')}</strong>
                            <div style={{ fontSize: '0.85rem', color: '#4A4A4A', marginTop: '0.15em' }}>
                                {t('checkout.comment_sent_description')}
                            </div>
                        </div>
                    </div>
                )}
                {showPendingBanner && !this.state.commentSent && (
                    <div id="pending-payments">
                        <PendingPaymentBanner subscriptionUrl={this.getPaymentUrl()} />
                    </div>
                )}

                {/* El código de prueba (CY-T): sólo en la prueba y si a la agencia le sirve uno hoy. Aplicado, la
                    pantalla vuelve a pedir la suscripción para mostrar la fecha nueva. */}
                {isFreeTrial && <TrialCodeCard onRedeemed={() => this.loadSubscriptionData()}/>}

                {/* El id es el destino del off-ramp "podés bajar de plan" del modal de baja. */}
                <div id="plan-change">
                <PlanChangeSection
                    currentTier={subscriptionData.plan_tier}
                    isFreeTrial={isFreeTrial}
                    isExpired={isExpired}
                    subscriptionData={subscriptionData}
                    onStartCheckout={(data) => this.setState({ checkoutData: data })}
                />
                </div>

                {!isFreeTrial && (
                    <div id={showPendingBanner ? undefined : "pending-payments"}>
                        <BillingHistorySection
                            billingData={billingLoading ? null : billingData}
                            onRetry={() => this.loadBillingHistory()}
                            showPaymentDetails={isSuperAdmin}
                            onEmailsUpdated={this.handleEmailsUpdated}
                            onLiquidationUpdated={() => this.loadBillingHistory()}
                            onRefreshBilling={this.refreshBilling}
                            refreshingBilling={this.state.refreshingBilling}
                        />
                    </div>
                )}

                <CancelSubscriptionSection
                    isFreeTrial={subscriptionData.is_free_trial}
                    subscriptionData={subscriptionData}
                    cancellationRequest={this.state.cancellationRequest}
                    onCancellationChanged={this.handleCancellationChanged}
                    onGoToPlans={this.scrollToPlans}
                    onRetentionAccepted={this.handleRetentionAccepted}
                />
            </div>
        );
    }
}

export default withTranslation()(AgencySubscriptionContent);
